import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useSnackbar } from '@/composables/useSnackbar';
import { useTranslation } from '@/composables/useTranslation';
import type { UseFavoriteNavigationReturnType } from '@/types/composables/FavoriteNavigationTypes';

const STORAGE_KEY_SUPER_ADMIN = 'favorites-super-admin';
const STORAGE_KEY_COMPANY_ADMIN = 'favorites-company-admin';

const superAdminFavorites = ref<string[]>([]);
const companyAdminFavorites = ref<string[]>([]);
let initialized = false;
let isToggling = false;
const TOGGLE_COOLDOWN_MS = 300;

export const useFavoriteNavigation = (): UseFavoriteNavigationReturnType => {
    const router = useRouter();
    const { handleSnackbarSuccess, showSnackbarMessage } = useSnackbar();
    const { t } = useTranslation();

    const context = computed(() => {
        const path = router.currentRoute.value.path;

        return path.startsWith('/super-admin') ? 'super-admin' : 'company-admin';
    });

    const currentFavorites = computed(() => {
        return context.value === 'super-admin' ? superAdminFavorites.value : companyAdminFavorites.value;
    });

    const loadFavorites = () => {
        const superAdminData = localStorage.getItem(STORAGE_KEY_SUPER_ADMIN);
        const companyAdminData = localStorage.getItem(STORAGE_KEY_COMPANY_ADMIN);

        superAdminFavorites.value = superAdminData ? JSON.parse(superAdminData) : [];
        companyAdminFavorites.value = companyAdminData ? JSON.parse(companyAdminData) : [];
    };

    const saveFavorites = () => {
        localStorage.setItem(STORAGE_KEY_SUPER_ADMIN, JSON.stringify(superAdminFavorites.value));
        localStorage.setItem(STORAGE_KEY_COMPANY_ADMIN, JSON.stringify(companyAdminFavorites.value));
    };

    const isFavorite = (key: string): boolean => {
        return currentFavorites.value.includes(key);
    };

    const toggleFavorite = async (key: string): Promise<void> => {
        if (isToggling) {
            return; // Ignore if toggle is in progress
        }

        isToggling = true;

        const favorites = context.value === 'super-admin' ? superAdminFavorites.value : companyAdminFavorites.value;

        const index = favorites.indexOf(key);

        if (index > -1) {
            favorites.splice(index, 1);
            showSnackbarMessage(handleSnackbarSuccess(t('messages.favoriteRemoved')));
        } else {
            favorites.push(key);
            showSnackbarMessage(handleSnackbarSuccess(t('messages.favoriteAdded')));
        }

        saveFavorites();

        setTimeout(() => {
            isToggling = false;
        }, TOGGLE_COOLDOWN_MS);
    };

    const getFavoriteKeys = (): string[] => {
        return [...currentFavorites.value];
    };

    const canAddToFavorite = (): boolean => {
        return router.currentRoute.value.meta.canAddToFavorite === true;
    };

    if (!initialized) {
        loadFavorites();
        initialized = true;
    }

    return {
        isFavorite,
        toggleFavorite,
        getFavoriteKeys,
        canAddToFavorite,
    };
};
